

import { getNflTeam } from '@/config/nfl-teams';
import { stripeColor, SURFACE_2 } from '@/lib/team-stripe';
import { weekShortLabel } from '@/lib/week-label';
import type { SurvivorUsedTeam } from '@/services/events/main-event';
import { getAbbrev } from './utils';


// ============================================================================
// USED TEAMS STRIP (Survivor)
// ============================================================================

/**
 * Horizontal wrap strip of teams the entrant has already burned — a team can
 * never be reused all season, so these render dimmed with the week they were
 * spent. Renders nothing until at least one team has been used.
 *
 * Identity is carried by the abbreviation plus a club colour stripe, not a
 * helmet: at chip scale the art was illegible, and it duplicated the
 * abbreviation sitting next to it.
 */
export function UsedTeamsStrip({ usedTeams }: { usedTeams: SurvivorUsedTeam[] }) {
  if (usedTeams.length === 0) return null;

  const sorted = [...usedTeams].sort((a, b) => a.round - b.round);

  return (
    <div>
      <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider block mb-2">
        Teams used ({usedTeams.length}/32)
      </span>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((ut) => {
          const nfl = getNflTeam(ut.team);
          return (
            <span
              key={`${ut.team}-${ut.round}`}
              className="flex items-center gap-1.5 pl-0 pr-2 py-1 rounded-md bg-surface-2 border border-surface-3 opacity-70 overflow-hidden"
            >
              {/* A colour stripe rather than a helmet. At this size the helmet
                  art was unreadable AND said the same thing as the abbreviation
                  beside it; the club's own colour is legible at 3px and adds a
                  scanning cue the abbreviation alone does not.

                  Routed through stripeColor against SURFACE_2, not painted raw:
                  six clubs have a near-black primary that vanishes on this chip
                  otherwise. The surface matters — Pittsburgh clears the bar on
                  the pick button (surface-3) and fails on this darker one. */}
              <span
                aria-hidden="true"
                className="w-[3px] self-stretch rounded-full"
                style={{
                  backgroundColor: nfl
                    ? stripeColor(nfl.primary, SURFACE_2)
                    : 'var(--color-surface-4)',
                }}
              />
              <span className="text-[10px] font-bold text-warm-gray">{getAbbrev(ut.team)}</span>
              <span className="text-[9px] text-warm-gray/50">{weekShortLabel(ut.round)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
